import React, { useState, useEffect, useCallback } from 'react';
import { getReglasCredito, deleteRegla } from '../services/reglaCreditoService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, Search, ChevronLeft, ChevronRight, ShieldCheck, DollarSign, Clock, Percent } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const ReglaCreditoList = () => {
    const [reglas, setReglas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { token } = useAuth();

    const fetchReglas = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getReglasCredito(token);
            setReglas(data);
        } catch (error) {
            toast.error('Error al cargar reglas de crédito');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchReglas();
    }, [fetchReglas]);

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar esta regla de crédito?')) return;
        try {
            await deleteRegla(token, id);
            toast.success('Regla eliminada correctamente');
            fetchReglas();
        } catch (error) {
            toast.error('Error al eliminar la regla.');
        }
    };

    const filteredReglas = reglas.filter(r => {
        const matchesSearch =
            (r.lineaCredito?.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.moneda?.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.categoria?.nombreCategoria || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const formatMoney = (amount) => {
        return new Intl.NumberFormat('es-PY', { style: 'decimal' }).format(amount);
    };

    return (
        <div className="max-w-[98%] mx-auto bg-white shadow-sm border border-gray-200 mb-10 overflow-hidden rounded-sm animate-in fade-in duration-500">
            <Toaster position="top-right" />

            {/* Header Enterprise Style */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion#financieros')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold tracking-tight uppercase italic flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-blue-400" />
                        Mantenimiento: Reglas de Crédito (Product Factory)
                    </h1>
                </div>

                <Link
                    to="/parametrizacion/reglas-credito/nuevo"
                    className="flex items-center gap-1.5 transition-all rounded-sm px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-[0.85rem] font-bold uppercase"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVA REGLA
                </Link>
            </div>

            {/* Controles */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <input
                            type="text"
                            placeholder="Buscar por línea o moneda..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-white border border-gray-300 pl-8 pr-3 py-1.5 w-72 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm shadow-sm uppercase font-bold"
                        />
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5 group-focus-within:text-blue-500" />
                    </div>
                </div>

                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    {loading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                    CONFIGURACIÓN DE LÍMITES Y TASAS POR PRODUCTO
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto relative">
                <table className="min-w-full text-left border-collapse table-fixed bg-white">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-[11px] font-bold uppercase tracking-wider">
                            <th className="px-3 py-3 w-16 text-center border-r border-gray-100 sticky left-0 bg-[#f8fafc] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">ID</th>
                            <th className="px-4 py-3 w-48 border-r border-gray-100">Línea de Crédito</th>
                            <th className="px-4 py-3 w-32 border-r border-gray-100 text-center">Moneda</th>
                            <th className="px-4 py-3 w-40 border-r border-gray-100">Categoría</th>
                            <th className="px-4 py-3 w-32 border-r border-gray-100 text-center">Riesgo</th>
                            <th className="px-4 py-3 w-48 border-r border-gray-100">Nivel Fidelidad</th>
                            <th className="px-4 py-3 w-36 border-r border-gray-100 text-right">Monto Mín</th>
                            <th className="px-4 py-3 w-36 border-r border-gray-100 text-right">Monto Máx</th>
                            <th className="px-4 py-3 w-36 border-r border-gray-100 text-right">Incremento M.</th>
                            <th className="px-4 py-3 w-24 border-r border-gray-100 text-center">Plazo Mín</th>
                            <th className="px-4 py-3 w-24 border-r border-gray-100 text-center">Plazo Máx</th>
                            <th className="px-4 py-3 w-28 border-r border-gray-100 text-center">Incremento P.</th>
                            <th className="px-4 py-3 w-24 border-r border-gray-100 text-center">Tasa Comp.</th>
                            <th className="px-4 py-3 w-24 border-r border-gray-100 text-center">Tasa Mor.</th>
                            <th className="px-4 py-3 w-24 border-r border-gray-100 text-center">Antigüedad</th>
                            <th className="px-3 py-3 w-32 text-center sticky right-0 bg-[#f8fafc] shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-100 z-10 font-black">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-[11px] text-gray-700 font-semibold uppercase">
                        {loading && reglas.length === 0 ? (
                            <tr><td colSpan="16" className="px-4 py-10 text-center text-gray-400 font-bold uppercase italic border-b border-gray-200">Buscando parámetros en el Product Factory...</td></tr>
                        ) : filteredReglas.length === 0 ? (
                            <tr><td colSpan="16" className="px-4 py-10 text-center text-gray-400 font-bold uppercase italic border-b border-gray-200">No se encontraron definiciones.</td></tr>
                        ) : (
                            filteredReglas.map((r) => (
                                <tr key={r.id} className="border-b border-gray-100 hover:bg-[#f1f5f9] transition-colors group">
                                    <td className="px-3 py-2.5 text-center font-mono text-[11px] text-gray-400 border-r border-gray-100 sticky left-0 bg-white group-hover:bg-[#f1f5f9] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                        {r.id}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 font-bold text-slate-800">
                                        {r.lineaCredito?.nombre}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center">
                                        {r.moneda?.codigoIso}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100">
                                        {r.categoria?.nombreCategoria || '--'}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center font-black">
                                        {r.calificacionRiesgo?.codigo || '--'}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100">
                                        {r.nivelFidelidad?.descripcion || '--'}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-right font-bold text-gray-900">
                                        {formatMoney(r.montoDesde)}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-right font-bold text-blue-700">
                                        {formatMoney(r.montoHasta)}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-right text-gray-500">
                                        {formatMoney(r.montoIncremento || 0)}
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center">
                                        {r.plazoMinimo} MESES
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center font-bold text-blue-700">
                                        {r.plazoMaximo} MESES
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center text-gray-500">
                                        {r.plazoIncremento} MESES
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center font-black text-gray-900">
                                        {r.tasaCompensatoria}%
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center font-black text-red-600">
                                        {r.tasaPunitoria}%
                                    </td>
                                    <td className="px-4 py-2.5 border-r border-gray-100 text-center">
                                        {r.antiguedadMinima || 0} MESES
                                    </td>
                                    <td className="px-3 py-2.5 text-center sticky right-0 bg-white shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-100 group-hover:bg-[#f1f5f9] z-10">
                                        <div className="flex justify-center items-center gap-2 font-bold text-[10px] tracking-tight">
                                            <button
                                                onClick={() => navigate(`/parametrizacion/reglas-credito/${r.id}/editar`)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors uppercase"
                                            >
                                                EDITAR
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleDelete(r.id)}
                                                className="text-red-500 hover:text-red-700 transition-colors uppercase"
                                            >
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <div className="p-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-[0.875rem] text-gray-500 uppercase font-bold">
                <div>
                    Mostrando <span className="text-gray-700">{filteredReglas.length}</span> parámetros vigentes
                </div>
                <div className="flex items-center gap-1">
                    <button disabled className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">Anterior</button>
                    <div className="px-4 py-1 border border-blue-600 bg-blue-600 text-white font-bold text-xs rounded-sm">1</div>
                    <button disabled className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">Siguiente</button>
                </div>
            </div>
        </div >
    );
};

export default ReglaCreditoList;
